import { decryptUserStorage, setUser } from '@reducers/UserSlice';
import { routePermissions, userConstats } from '@utils/constants';
import axios, { AxiosRequestConfig } from 'axios';
import CryptoJS from 'crypto-js';

import { store } from 'store/store';

import { authEndpoints } from '../utils/constants/apis';
import { REDIRECT_URL, TOKEN_KEY, USER_KEY } from '../utils/constants/localStorageKeys';
import { User } from '../utils/models/User';

import { LocalStorage } from './LocalStorage';

export const BASE_URL = import.meta.env.VITE_BASE_URL;
export const BASE_URL_VISUAL = import.meta.env.VITE_BASE_URL_VISUAL;

let isRefreshing = false;
let failedQueue: any = [];

const processQueue = (error: any, token = null) => {
  failedQueue.forEach((prom: any) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const addToken = (config: AxiosRequestConfig) => {
  const token = LocalStorage.get<string>(TOKEN_KEY);
  const firebaseToken = LocalStorage.get<string>('firebaseToken');

  if (token) {
    config.headers!.Authorization = `Bearer ${token}`;
  }
  if (firebaseToken) {
    config.headers!.firebaseToken = firebaseToken;
  }
  return config;
};

const resentRequest = async (err: any) => {
  const originalRequest = err?.config;

  // eslint-disable-next-line no-underscore-dangle
  if (err?.response?.status === 401 && !originalRequest._retry) {
    if (!LocalStorage.get<string>(TOKEN_KEY)) {
      LocalStorage.set(REDIRECT_URL, window.location.href);
      window.location.replace(window.location.origin);
    }

    if (isRefreshing) {
      // If I'm refreshing the token I send request to a queue
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axios(originalRequest);
      });
    }

    // If header of the request has changed, it means I've refreshed the token
    const token = LocalStorage.get<string>(TOKEN_KEY);

    if (originalRequest.headers.Authorization !== `Bearer ${token}`) {
      originalRequest.headers.Authorization = `Bearer ${token}`;

      return Promise.resolve(axios(originalRequest));
    }
    // eslint-disable-next-line no-underscore-dangle
    originalRequest._retry = false; // mark request a retry
    isRefreshing = false; // set the refreshing var to true

    // If none of the above, refresh the token and process the queue
    return new Promise((resolve, reject) => {
      axios
        .get(BASE_URL + authEndpoints.REFRESH, {
          withCredentials: true
        }) // The method that refreshes my token
        .then(response => {
          let isReload = false;
          const resCopy: User = response.data?.data;
          const user = decryptUserStorage as User;
          const branch = resCopy?.roleTokens?.find(item => (item?.branch?.id || item?.branch) === user?.branchId);
          const oldRoutePermissions = routePermissions.filter(i => user?.role?.permissions?.includes(i));

          localStorage.setItem('refresh_response', JSON.stringify(response));

          if (typeof user === 'object') {
            const set1 = new Set(oldRoutePermissions);
            const set2 = new Set(branch?.role?.permissions || response.data?.data?.role?.permissions || []);

            const uniqueInArray1 = oldRoutePermissions.filter(item => !set2.has(item));
            const uniqueInArray2 = (branch?.role?.permissions || response.data?.data?.role?.permissions)?.filter(
              (item: number) => !set1.has(item)
            );

            const uniqueValues = [...uniqueInArray1, ...uniqueInArray2];

            isReload = routePermissions?.some(i => uniqueValues?.includes(i));
          }

          LocalStorage.set(TOKEN_KEY, branch?.accessToken || response.data?.data?.accessToken);

          processQueue(null, branch?.accessToken || response.data?.data?.accessToken);

          const encrypt = CryptoJS.AES.encrypt(
            JSON.stringify({
              ...response.data?.data,
              role: branch?.role || response.data?.data?.role,
              branchId: user?.branchId
            }),
            userConstats.CODE_KEY
          ).toString();

          store.dispatch(
            setUser({
              ...response.data?.data,
              role: branch?.role || response.data?.data?.role,
              branchId: user?.branchId
            })
          );

          LocalStorage.set(USER_KEY, encrypt);

          originalRequest.headers.Authorization = `Bearer ${branch?.accessToken || response.data?.data?.accessToken}`;

          resolve(axios(originalRequest)); // Resolve current

          if (isReload) {
            window.location.reload();
          }
        })
        .catch(err => {
          LocalStorage.remove(TOKEN_KEY); // The method that removes my token from localstorage/Redux/whatever
          LocalStorage.remove(USER_KEY);
          LocalStorage.set(REDIRECT_URL, window.location.pathname);
          window.location.replace(window.location.origin);
          processQueue(err, null);
          reject(err);
        })
        .finally(() => {
          isRefreshing = false;
        });
    });
  }

  throw err;
};

export const $api = axios.create({ baseURL: BASE_URL });

export const $apiCustom = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  withCredentials: true
});

$api.interceptors.request.use(addToken);

$api.interceptors.response.use(undefined, resentRequest);

export const $apiVisual = axios.create({ baseURL: BASE_URL_VISUAL });
